import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Headset, Phone, ChatCircleText, ChatText, X } from '@phosphor-icons/react';

const BUSINESS_PHONE = '919156986500'; // country code + number, no symbols — used for tel:/wa.me/sms: links
const DISPLAY_PHONE = '+91 91569 86500';

// Small, draggable floating help button, present on every customer screen
// (mounted once in CustomerApp, outside the route switch, so position and
// open/closed state naturally persist across navigation).
//
// Deliberately minimal in size — just enough to be identifiable, not a
// large intrusive button. Draggable so the customer can move it out of the
// way of whatever they're actually doing. Uses framer-motion's built-in
// drag/tap gesture distinction: onTap only ever fires for a genuine tap
// that didn't move past the library's own drag threshold, so repositioning
// the button can never accidentally open it, and vice versa.
export default function FloatingHelpButton() {
  const [open, setOpen] = useState(false);
  const constraintsRef = useRef(null);

  const handleCall = () => { window.location.href = `tel:${BUSINESS_PHONE}`; };
  const handleWhatsApp = () => { window.open(`https://wa.me/${BUSINESS_PHONE}`, '_blank'); };
  const handleSms = () => { window.location.href = `sms:${BUSINESS_PHONE}`; };

  return (
    <div ref={constraintsRef} className="fixed inset-0 pointer-events-none z-[70]">
      <motion.button
        drag
        dragConstraints={constraintsRef}
        dragElastic={0.08}
        dragMomentum={false}
        onTap={() => setOpen((v) => !v)}
        whileTap={{ scale: 0.92 }}
        className="absolute bottom-20 right-4 pointer-events-auto w-11 h-11 rounded-full bg-sspice shadow-lg flex items-center justify-center touch-none"
        aria-label="Help"
        data-testid="floating-help-btn"
      >
        <Headset size={20} weight="fill" className="text-white" />
      </motion.button>

      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop — tapping outside the sheet closes it */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setOpen(false)}
              className="absolute inset-0 bg-black/40 pointer-events-auto"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.3, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.3, y: 40 }}
              transition={{ type: 'spring', stiffness: 300, damping: 22 }}
              className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-32px)] max-w-sm bg-white rounded-2xl shadow-xl p-5 pointer-events-auto"
              data-testid="help-sheet"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X size={18} />
              </button>
              <p className="font-semibold text-gray-900 mb-0.5">Need help?</p>
              <p className="text-sm text-gray-500 mb-4">Reach us directly at {DISPLAY_PHONE}</p>

              <div className="flex flex-col gap-2">
                <button
                  onClick={handleCall}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                  data-testid="help-call-btn"
                >
                  <Phone size={20} weight="fill" className="text-sspice" />
                  <span className="text-sm font-medium text-gray-800">Call us</span>
                </button>
                <button
                  onClick={handleWhatsApp}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                  data-testid="help-whatsapp-btn"
                >
                  <ChatCircleText size={20} weight="fill" className="text-green-600" />
                  <span className="text-sm font-medium text-gray-800">WhatsApp us</span>
                </button>
                <button
                  onClick={handleSms}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                  data-testid="help-sms-btn"
                >
                  <ChatText size={20} weight="fill" className="text-gray-500" />
                  <span className="text-sm font-medium text-gray-800">Send an SMS</span>
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
